/**
 * StoreBadges — "Get it on Google Play" / "Download on the App Store" badges.
 * Clicking either badge opens a lightweight "coming soon" popup since the
 * native apps aren't published yet.
 */
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";

function GooglePlayIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id="gp-grad" x1="2" y1="2" x2="20" y2="12" gradientUnits="userSpaceOnUse">
          <stop offset="0"    stopColor="#00E3FF" />
          <stop offset="0.35" stopColor="#33D375" />
          <stop offset="0.68" stopColor="#FFD500" />
          <stop offset="1"    stopColor="#FF3A44" />
        </linearGradient>
      </defs>
      <path d="M4 2.6C3.6 3 3.4 3.6 3.4 4.3V19.7C3.4 20.4 3.6 21 4 21.4L4.1 21.5L12.6 13V12.8L4.1 4.3L4 2.6Z" fill="url(#gp-grad)" />
      <path d="M4 2.6L12.6 12L15.5 9.1L5.3 3.3C4.8 3 4.3 2.9 4 2.6Z" fill="#FF3A44" />
      <path d="M4 21.4C4.5 21.8 5.2 21.8 5.9 21.5L15.5 15.9L12.6 13L4 21.4Z" fill="#33D375" />
      <path d="M15.5 9.1L18.9 11C19.7 11.5 19.7 12.5 18.9 13L15.5 15.9L12.6 13L15.5 9.1Z" fill="#FFD500" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
      <path d="M16.365 1.43c0 1.14-.462 2.257-1.15 3.056-.79.923-2.06 1.634-3.13 1.634-.13 0-.26-.02-.35-.03-.02-.13-.04-.28-.04-.43 0-1.09.5-2.19 1.17-2.95.83-.94 2.21-1.65 3.32-1.68.06.13.09.27.09.4zm3.36 15.5c-.6 1.36-.88 1.97-1.65 3.17-1.07 1.67-2.58 3.75-4.45 3.77-1.66.02-2.09-1.08-4.34-1.07-2.25.01-2.72 1.09-4.38 1.07-1.87-.02-3.3-1.89-4.37-3.56-2.99-4.65-3.3-10.11-1.46-13.02 1.31-2.07 3.38-3.28 5.32-3.28 1.98 0 3.22 1.09 4.86 1.09 1.59 0 2.55-1.09 4.86-1.09 1.72 0 3.55.94 4.85 2.56-4.27 2.34-3.58 8.44.86 10.36z" />
    </svg>
  );
}

function ComingSoonModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(5,8,25,0.72)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
        animation: "storeBadgeFadeIn 0.15s ease both",
      }}
    >
      <style>{`
        @keyframes storeBadgeFadeIn { from { opacity:0; } to { opacity:1; } }
        @keyframes storeBadgePopIn { from { opacity:0; transform:translateY(10px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 360,
          background: "rgba(17,21,54,0.96)",
          border: "1px solid rgba(139,92,246,0.3)",
          borderRadius: 18,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          padding: "28px 26px 24px",
          textAlign: "center",
          animation: "storeBadgePopIn 0.2s cubic-bezier(.23,1,.32,1) both",
        }}
      >
        <div style={{
          width: 52, height: 52, borderRadius: 14, margin: "0 auto 16px",
          background: "linear-gradient(135deg, #6366F1 0%, #7C3AED 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 24, boxShadow: "0 0 20px rgba(99,102,241,0.5)",
        }}>
          🚀
        </div>
        <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 800, color: "#fff" }}>
          Coming Soon
        </h3>
        <p style={{ margin: "0 0 20px", fontSize: 13.5, lineHeight: 1.65, color: "rgba(255,255,255,0.6)" }}>
          Our mobile app is on its way to Google Play and the App Store. Stay tuned!
        </p>
        <button
          onClick={onClose}
          style={{
            width: "100%", height: 42, borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#6366F1,#8B5CF6)",
            color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}
        >
          Got it
        </button>
      </div>
    </div>,
    document.body
  );
}

const badgeStyle = {
  display: "flex", alignItems: "center", gap: 8,
  background: "#000", border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 9, padding: "6px 12px", cursor: "pointer",
  transition: "border-color 0.15s, transform 0.15s",
};

export function GooglePlayBadge() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Get it on Google Play — coming soon"
        style={badgeStyle}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; e.currentTarget.style.transform = "translateY(0)"; }}
      >
        <GooglePlayIcon />
        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.15 }}>
          <span style={{ fontSize: 8.5, color: "rgba(255,255,255,0.75)", letterSpacing: 0.3 }}>GET IT ON</span>
          <span style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>Google Play</span>
        </span>
      </button>
      {open && <ComingSoonModal onClose={() => setOpen(false)} />}
    </>
  );
}

export function AppStoreBadge() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Download on the App Store — coming soon"
        style={badgeStyle}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; e.currentTarget.style.transform = "translateY(0)"; }}
      >
        <AppleIcon />
        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.15 }}>
          <span style={{ fontSize: 8.5, color: "rgba(255,255,255,0.75)", letterSpacing: 0.3 }}>Download on the</span>
          <span style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>App Store</span>
        </span>
      </button>
      {open && <ComingSoonModal onClose={() => setOpen(false)} />}
    </>
  );
}

export function StoreBadges({ gap = 10 }) {
  return (
    <div style={{ display: "flex", gap, flexWrap: "wrap" }}>
      <GooglePlayBadge />
      <AppStoreBadge />
    </div>
  );
}
